import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';

import Box from '~/common/components/Box';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import { LOAN_PURPOSE_TYPE_BY_NAME } from '~/common/constants';
import type { LoanSummary } from '~/common/hooks/useGetLoanSummary';
import MUIIconButton from '~/common/mui/Buttons/MUIButton/MUIIconButton';
import Grid from '~/common/mui/Grids/GridItem';
import { formatCurrency } from '~/common/util/format';
import lang from '~/modals/LoanSummaryDialog/language.json';

import {
  LabelRowGroup,
  LabelText,
  SpacedRow,
  StyledDivider,
  TopLabel,
} from './Styled';

const determineMaskingText = (ssn: string | undefined, show: boolean) => {
  if (ssn) {
    return show ? ssn : SSN_MASK;
  }
  return '';
};

const SSN_MASK = 'XXX-XX-XXXX';

type SsnStackProps = {
  ssn: string;
};

const SsnStack = ({ ssn }: SsnStackProps) => {
  const [showSsn, setShowSsn] = useState(false);
  const theme = useTheme();

  return (
    <Stack direction="row" sx={{ alignItems: 'center' }}>
      <Text
        sx={{
          minWidth: '105px',
          display: 'inline-block',
          color: theme.palette.gray[600],
        }}
      >
        {determineMaskingText(ssn, showSsn)}
      </Text>

      {ssn && (
        <MUIIconButton
          aria-label={showSsn ? 'Hide SSN' : 'Show SSN'}
          color="primary"
          onClick={() => setShowSsn((prev) => !prev)}
          size="small"
          sx={{
            alignSelf: 'center',
          }}
        >
          {ssn &&
            (showSsn ? (
              <VisibilityOutlinedIcon />
            ) : (
              <VisibilityOffOutlinedIcon />
            ))}
        </MUIIconButton>
      )}
    </Stack>
  );
};

type LoanSummaryLoanTabProps = Readonly<{
  loanInfo: LoanSummary['loanInfo'];
  propertyData: LoanSummary['propertyData'];
  miscellaneousInfo: LoanSummary['miscellaneousInfo'];
  borrowerData: {
    borrowers?: Array<{
      firstName?: string;
      middleName?: string;
      lastName?: string;
      suffixName?: string;
      socialSecurityNumber?: string;
    }>;
  };
}>;

export function LoanSummaryLoanTab({
  loanInfo,
  propertyData,
  miscellaneousInfo,
  borrowerData,
}: LoanSummaryLoanTabProps) {
  return (
    <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'row' }}>
      <Grid
        id="loan-details"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mt: '1rem',
          minWidth: '340px',
          mr: '1.5rem',
        }}
      >
        <TopLabel>{lang.LOAN_DETAILS}</TopLabel>
        <LabelRowGroup>
          <SpacedRow>
            <LabelText>{lang.LOAN_PRODUCT}</LabelText>
            <Text>
              {loanInfo?.loanProductTypeDescription ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
          <StyledDivider />
          <SpacedRow
            sx={{
              wordWrap: 'normal',
            }}
          >
            <LabelText>{lang.LOAN_PURPOSE}</LabelText>
            <Text sx={{ marginTop: '0 !important' }}>
              {propertyData.purposeType ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
          {propertyData.purposeType == 'Refinance 1st Mortgage' && (
            <>
              <StyledDivider />
              <SpacedRow
                sx={{
                  wordWrap: 'normal',
                }}
              >
                <LabelText>{lang.REFI_PURPOSE}</LabelText>
                <Text sx={{ marginTop: '0 !important' }}>
                  {propertyData.refiPurpose ?? lang.PLACEHOLDER}
                </Text>
              </SpacedRow>
            </>
          )}
        </LabelRowGroup>
        <LabelRowGroup>
          <SpacedRow>
            <LabelText>{lang.BASE_LOAN_AMOUNT}</LabelText>
            <Text>
              {formatCurrency(loanInfo.baseLoanAmount) ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
          <StyledDivider />
          <SpacedRow>
            <LabelText>{lang.TOTAL_LOAN_AMOUNT}</LabelText>
            <Text>
              {formatCurrency(loanInfo.totalLoanAmount) ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
          {propertyData?.purposeType !==
            LOAN_PURPOSE_TYPE_BY_NAME['REFINANCE_FIRST_MORTGAGE'] && (
            <>
              <StyledDivider />
              <SpacedRow>
                <LabelText>{lang.SALES_PRICE}</LabelText>
                <Text>
                  {formatCurrency(loanInfo.salesPrice) ?? lang.PLACEHOLDER}
                </Text>
              </SpacedRow>
            </>
          )}
          <StyledDivider />
          <SpacedRow>
            <LabelText>{lang.APPRAISED_VALUE}</LabelText>
            <Text>
              {formatCurrency(loanInfo.appraisedValue) ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
        </LabelRowGroup>
        <LabelRowGroup>
          <SpacedRow>
            <LabelText>
              {loanInfo?.creditScoreCategory === 'VantageScore'
                ? lang.VANTAGE
                : lang.FICO}
            </LabelText>
            <Text>{loanInfo?.fico?.toString() ?? lang.PLACEHOLDER}</Text>
          </SpacedRow>
          <StyledDivider />
          <SpacedRow>
            <LabelText>{lang.INTEREST_RATE}</LabelText>
            <Text>
              {loanInfo?.interestRate === undefined
                ? lang.PLACEHOLDER
                : `${loanInfo?.interestRate}%`}
            </Text>
          </SpacedRow>
          <StyledDivider />
          <SpacedRow>
            <LabelText>{lang.PITIA}</LabelText>
            <Text>
              {formatCurrency(miscellaneousInfo.piti) ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
          <StyledDivider />
          <SpacedRow>
            <LabelText>{lang.CASH_TO_FROM_BORROWER}</LabelText>
            <Text>
              {formatCurrency(loanInfo.cashToFromBorrower) ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
          <StyledDivider />
          <SpacedRow>
            <LabelText>{lang.TITLE_TYPE}</LabelText>
            <Text>{loanInfo.tracTypeDescription ?? lang.PLACEHOLDER}</Text>
          </SpacedRow>
        </LabelRowGroup>
      </Grid>

      <Grid
        id="borrower(s)"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: '1rem',
          minWidth: '340px',
        }}
      >
        <TopLabel>{lang.BORROWERS}</TopLabel>
        {Array.isArray(borrowerData?.borrowers) &&
          borrowerData.borrowers.map((b, i) => (
            <Stack
              key={JSON.stringify(b)}
              sx={{
                mb: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Text>
                {[b.firstName, b.middleName, b.lastName, b.suffixName]
                  .filter(Boolean)
                  .join(' ')}
              </Text>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'grey.500',
                }}
              >
                <SsnStack ssn={b.socialSecurityNumber} />
                {i === 0 && (
                  <Text
                    sx={{
                      fontStyle: 'bold',
                      ml: 0.5,
                      color: 'black',
                      fontWeight: '500',
                    }}
                  >
                    {lang.PRIMARY_BORROWER}
                  </Text>
                )}
              </Box>
            </Stack>
          ))}
      </Grid>
    </Grid>
  );
}
